<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { copyToClipboard } from '@common/Common'

    import type { CriteriaPanelistReadModel } from '@Evaluation/CriteriaSurveys/Domain/readmodels'
    import type { CriteriaSurveyAccessLinksViewModel } from '@Evaluation/CriteriaSurveys/Components/CriteriaSurveyAccessLinks'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: CriteriaSurveyAccessLinksViewModel
    }>()

    const emit = defineEmits<{
        (e: 'download-links', surveyId: number): void
        (e: 'show-email-sender', surveyId: number, projectUserIds: number[]): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value.opened)
    const surveyId = computed(() => vm.value.surveyId)
    const initialized = computed(() => vm.value.data != null)

    const panelists = computed(() => vm.value.data?.Panelists ?? [])

    // ---------------- actions ----------------

    function downloadLinks()
    {
        emit('download-links', surveyId.value)
    }

    function emailOne(panelist: CriteriaPanelistReadModel)
    {
        emit('show-email-sender', surveyId.value, [panelist.ProjectUserId])
    }

    function emailAll()
    {
        emit(
            'show-email-sender',
            surveyId.value,
            panelists.value.map(p => p.ProjectUserId)
        )
    }

    function copyAccessUrlToClipboard(panelist: CriteriaPanelistReadModel)
    {
        copyToClipboard(panelist.AccessUrl)

        const originalUrl = panelist.AccessUrl
        panelist.AccessUrl = 'Copied to clipboard'

        const handle = setTimeout(() =>
        {
            panelist.AccessUrl = originalUrl
            clearTimeout(handle)
        }, 500)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Access links</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h3>

                        <div v-if="initialized">
                            <table class="table table-hover">
                                <thead class="thead-light">
                                    <tr>
                                        <th>
                                            Panelist
                                        </th>
                                        <th>
                                            Access link
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="p in panelists" :key="p.ProjectUserId">
                                        <td>
                                            <span>{{p.Name}}</span><br />
                                            <span class="text-muted" v-if="p.HasEmail">{{p.Email}}</span>
                                            <span class="text-danger" v-if="!p.HasEmail">Email not provided</span>
                                        </td>
                                        <td class="align-middle">
                                            <div class="input-group">

                                                <input type="text" class="form-control" readonly v-model="p.AccessUrl" />
                                                <div class="input-group-append">
                                                    <button class="btn btn-outline-secondary" type="button" title="Copy to clipboard" @click="copyAccessUrlToClipboard(p)">
                                                        <i class="fa fa-clipboard" aria-hidden="true"></i>
                                                    </button>
                                                    <button class="btn btn-outline-secondary" type="button" title="Send link via email" :disabled="!p.HasEmail" @click="emailOne(p)">
                                                        <i class="fa-regular fa-envelope"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button @click="downloadLinks" :disabled="!initialized" type="button" class="btn btn-outline-secondary">Download links</button>
                        <button @click="emailAll" :disabled="!initialized" type="button" class="btn btn-outline-secondary">Email all</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>